import { env } from '@/config/env'
import { sessionResponseSchema } from './vfsClientEnvelope'
import { vfsClientRequest } from './vfsClient'
import { vfsClientRoutes } from './routes'

/**
 * The browser's session with VFS's backend — guide §2, ADR 0018.
 *
 * There is still no credential in the bundle. `POST /session` mints an anonymous
 * session, the backend escalates it as the journey progresses (`initiate` binds it to
 * one application, the officer sign-in attaches the officer), and every route asserts
 * the binding. What changed in ADR 0018 is only how that session travels: the shared
 * gateway in front of the API drops `Set-Cookie` and refuses credentialed CORS, so the
 * session JWT comes back in the response body and goes out in `x-sid-key` — the header
 * that gateway already forwards — instead of riding in an HttpOnly cookie.
 *
 * So this module now holds the token. The backend re-issues it whenever the session
 * changes (an escalation, or the sliding renewal past half its life) and returns the
 * new one in `metadata.sessionToken`; the transport hands it to `setSessionToken`. It
 * is kept in sessionStorage as well as memory so a page refresh resumes the
 * application, as the wizard's own state does (ADR 0008). It dies with the tab, and
 * it is bound to one application and short-lived on the server.
 */

const STORAGE_KEY = 'vfs.sessionToken'

function readStoredToken(): string | undefined {
  try {
    return sessionStorage.getItem(STORAGE_KEY) ?? undefined
  } catch {
    return undefined
  }
}

let token: string | undefined = readStoredToken()

/** De-duplicates concurrent callers: a single wizard step fires several writes. */
let inFlight: Promise<void> | undefined

/** The `POST /session/delete` in flight, if any — awaited by the test reset so it cannot leak. */
let ending: Promise<void> | undefined

const listeners = new Set<() => void>()

/** The session token this tab presents in `x-sid-key`, if it has one. */
export function getSessionToken(): string | undefined {
  return token
}

/**
 * Adopts a token the backend issued. Called with the bootstrap's token and with every
 * `metadata.sessionToken` the transport sees, so an escalated session replaces the
 * unbound one before the next request needs its binding.
 */
export function setSessionToken(next: string): void {
  token = next
  try {
    sessionStorage.setItem(STORAGE_KEY, next)
  } catch {
    // Storage refused (private mode, quota): the session still works for this page
    // load, it just will not survive a refresh.
  }
}

function clearSessionToken(): void {
  token = undefined
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // Nothing stored to remove.
  }
}

export function hasEstablishedSession(): boolean {
  return token !== undefined
}

/**
 * Makes sure this tab holds a session token.
 *
 * `POST /session` is idempotent and sliding on the backend: a token it still
 * recognises (the transport presents the current one) is re-issued with the same id
 * and bindings, only fresher. So calling this after a refresh or a 401 never throws
 * away the citizen's progress while the session is alive, and mints a clean
 * anonymous one when it is not.
 *
 * ⚠ `bootstrap: false` is load-bearing. The transport calls `ensureSession()` before
 * a request that has no session yet, and again on a 401 — a session request that
 * asked for either would recurse until the stack ran out.
 *
 * The token is adopted only on success: a bootstrap the rate limiter refused leaves
 * the next request to try again rather than believing a session exists.
 */
export function ensureSession(): Promise<void> {
  inFlight ??= vfsClientRequest({
    path: vfsClientRoutes.session,
    method: 'POST',
    parse: sessionResponseSchema,
    bootstrap: false,
  })
    .then((session) => {
      setSessionToken(session.sessionToken)
    })
    .finally(() => {
      inFlight = undefined
    })

  return inFlight
}

/**
 * Forgets the session. Best-effort and never rejects: every caller is a teardown —
 * Start over, Cancel, a rejected physical verification, the exits from the receipt —
 * on which a failed request is neither actionable nor worth delaying the navigation
 * for.
 *
 * The token is dropped first, which is what actually ends the session for this tab:
 * a stateless JWT cannot be revoked, and with nothing to present the next request
 * starts a fresh anonymous one. `POST /session/delete` still runs so the backend can
 * clear the cookie in deployments that use it.
 *
 * Skipped when this tab never held a session: there is nothing to end.
 */
export function endSession(): Promise<void> {
  if (token === undefined) return Promise.resolve()
  clearSessionToken()

  ending = vfsClientRequest({
    path: vfsClientRoutes.sessionDelete,
    method: 'POST',
    bootstrap: false,
  })
    .catch((cause: unknown) => {
      if (!env.isProduction) console.warn('[vfsclient] POST /session/delete failed', cause)
    })
    .finally(() => {
      ending = undefined
    })

  return ending
}

/**
 * Called by the transport when the server has stopped recognising this tab's
 * session — a 401 that a re-bootstrap did not cure, or a 403 `FORBIDDEN` saying the
 * session does not own the application the tab is working on. The wizard's expiry
 * watcher subscribes so the store can be emptied and the citizen told why.
 */
export function onSessionLost(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function emitSessionLost(): void {
  // The next journey starts with a fresh bootstrap rather than another 401.
  clearSessionToken()
  for (const listener of listeners) listener()
}

/**
 * Test hook. The token is module state shared by every test in a file, so without
 * this a session from one test would be presented by the next, and a `clear()` in
 * one test's setup would fire a real session delete into the next.
 */
export async function resetSessionTransport(): Promise<void> {
  clearSessionToken()
  listeners.clear()
  await ending
  await inFlight?.catch(() => undefined)
}
